import { type Editor } from '@tiptap/react';
import * as React from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { ChevronDownIcon } from '@/components/tiptap-editor/tiptap-icons/chevron-down-icon';
import { InsertImageIcon } from '@/components/tiptap-editor/tiptap-icons/insert-image-icon';
import { UploadImageIcon } from '@/components/tiptap-editor/tiptap-icons/upload-image-icon';
import { ZyGdocDrawingIcon } from '@/components/tiptap-editor/tiptap-icons/zy-gdoc-drawing-icon';
import { ImageIcon, Images } from 'lucide-react';

// --- Lib ---
import { isNodeInSchema } from '@/lib/tiptap-utils';
import { isSelectionTopNode } from '@/lib/zy-tiptap-utils';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/tiptap-editor/tiptap-ui-primitive/dropdown-menu';

import { ImageLibraryPickerModal } from '@/components/image-library/image-library-picker-modal';
import { insertImage } from '@/components/tiptap-editor/tiptap-ui/image-upload-button/use-image-upload';
import {
    canSeeImageLibrary,
    editorHasImageLibraryContext,
    insertImageFromLibrarySelection,
    openImageLibraryPageInNewTab,
} from '@/lib/image-from-library';
import type { MaImage } from '@/lib/zybooks-api';

export interface ImageOption {
    label: string;
    extensionName: string;
    icon: React.ComponentType<{ className?: string }>;
    /**
     * When true, opens the image library picker instead of running `command`.
     */
    opensImageLibrary?: boolean;
    check: (editor: Editor) => boolean;
    command?: (editor: Editor) => void;
}

export const imageOptions: ImageOption[] = [
    {
        label: 'Insert from library',
        extensionName: 'imageLibrary',
        icon: InsertImageIcon,
        check: () => true,
        opensImageLibrary: true,
    },
    {
        label: 'Open image library',
        extensionName: 'imageLibraryPage',
        icon: Images,
        check: () => true,
        command: (editor: Editor) => openImageLibraryPageInNewTab(editor),
    },
    {
        label: 'Upload Image',
        extensionName: 'imageUpload',
        icon: UploadImageIcon,
        check: () => true,
        command: (editor: Editor) => insertImage(editor),
    },
    {
        label: 'Draw Image',
        extensionName: 'zyGdocDrawing',
        icon: ZyGdocDrawingIcon,
        check: (editor: Editor) => isSelectionTopNode(editor),
        command: (editor: Editor) => editor.commands.insertZyGdocDrawing(),
    },
];

const atLeastOneImageOptionCanBeInserted = (editor: Editor | null) => {
    if (!editor) {
        return false;
    }

    return imageOptions.some(option => imageOptionShouldBeShown(option, editor));
};

const imageOptionShouldBeShown = (option: ImageOption, editor: Editor) => {
    // TODO: Remove this line once we want to show the image library option to all users
    if (option.extensionName === 'imageLibrary') return canSeeImageLibrary(editor);
    if (option.extensionName === 'imageLibraryPage') {
        return canSeeImageLibrary(editor) && editorHasImageLibraryContext(editor);
    }

    return isNodeInSchema(option.extensionName, editor);
};

export interface ImageDropdownMenuProps extends Omit<ButtonProps, 'type'> {
    /**
     * The TipTap editor instance.
     */
    editor?: Editor;

    onOpenChange?: (isOpen: boolean) => void;
}

export function useImageDropdownMenuState() {
    const [isOpen, setIsOpen] = React.useState(false);

    const handleOpenChange = React.useCallback((open: boolean, callback?: (isOpen: boolean) => void) => {
        setIsOpen(open);
        callback?.(open);
    }, []);

    return {
        isOpen,
        setIsOpen,
        handleOpenChange,
    };
}

export function ImageDropdownMenu({ editor: providedEditor, onOpenChange, ...props }: ImageDropdownMenuProps) {
    const { editor } = useTiptapEditor(providedEditor);

    const { isOpen, handleOpenChange } = useImageDropdownMenuState();
    const [isLibraryModalOpen, setIsLibraryModalOpen] = React.useState(false);

    const show = !!editor;
    const handleOnOpenChange = React.useCallback((open: boolean) => handleOpenChange(open, onOpenChange), [handleOpenChange, onOpenChange]);

    const handleOpenImageLibrary = React.useCallback(() => {
        handleOpenChange(false, onOpenChange);
        queueMicrotask(() => setIsLibraryModalOpen(true));
    }, [handleOpenChange, onOpenChange]);

    const handleLibraryImageSelected = React.useCallback(
        (image: MaImage) => {
            if (!editor) return;
            insertImageFromLibrarySelection(editor, image);
        },
        [editor],
    );

    const subjectName = editor?.state.doc.attrs.subjectName?.trim();
    const imageLibraryDefaultOnlyUnused = !subjectName;
    const imageLibraryDefaultSelectedSubjects = React.useMemo(() => (subjectName ? [subjectName] : []), [subjectName]);

    const handleImageClick = React.useCallback(
        (option: ImageOption) => {
            if (!editor) return;
            if (option.opensImageLibrary) {
                handleOpenImageLibrary();
                return;
            }
            option.command?.(editor);
            handleOpenChange(false, onOpenChange);
        },
        [editor, handleOpenChange, handleOpenImageLibrary, onOpenChange],
    );

    if (!show || !editor || !editor.isEditable) {
        return null;
    }

    return atLeastOneImageOptionCanBeInserted(editor) ? (
        <>
            <DropdownMenu
                open={isOpen}
                onOpenChange={handleOnOpenChange}
            >
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        data-style="ghost"
                        data-active-state="off"
                        role="button"
                        tabIndex={-1}
                        aria-label="Insert image"
                        tooltip="Image"
                        {...props}
                    >
                        <ImageIcon className="tiptap-button-icon" />
                        <ChevronDownIcon className="tiptap-button-dropdown-small" />
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent>
                    <Card>
                        <CardBody>
                            <DropdownMenuGroup>
                                {imageOptions.map(option => {
                                    if (!imageOptionShouldBeShown(option, editor)) {
                                        return null;
                                    }
                                    return (
                                        <DropdownMenuItem
                                            key={option.label}
                                            asChild
                                            disabled={!option.check(editor)}
                                        >
                                            <Button
                                                type="button"
                                                data-style="ghost"
                                                role="button"
                                                tabIndex={-1}
                                                disabled={!option.check(editor)}
                                                onClick={() => handleImageClick(option)}
                                            >
                                                <div className="flex-start flex w-full">
                                                    <option.icon className="mr-2 h-4 w-4" />
                                                    <span>{option.label}</span>
                                                </div>
                                            </Button>
                                        </DropdownMenuItem>
                                    );
                                })}
                            </DropdownMenuGroup>
                        </CardBody>
                    </Card>
                </DropdownMenuContent>
            </DropdownMenu>

            <ImageLibraryPickerModal
                open={isLibraryModalOpen}
                onOpenChange={setIsLibraryModalOpen}
                defaultOnlyUnused={imageLibraryDefaultOnlyUnused}
                defaultSelectedSubjects={imageLibraryDefaultSelectedSubjects}
                onImageSelected={handleLibraryImageSelected}
            />
        </>
    ) : null;
}

export default ImageDropdownMenu;
